/**
 * @fileoverview Structured help content for the inline "What are X?" affordance
 * on insight and practice chat cards. Data only — the DOM is built by
 * `js/chat/helpHintPanel.js`.
 *
 * Content shape:
 *   heading   - In-body H2. The panel opens with no chrome title (matching the
 *               insight detail panel), so this is the page's own title.
 *   lede      - One sentence that does the whole job if nothing else is read
 *   specimen  - Annotated figure of the card the user is looking at. Rows pair a
 *               gutter annotation with one or more card parts. Part types:
 *               label | title | body | pills | callout
 *   section   - Closing beat under its own H2: numbered steps (practices) or
 *               sources (insights)
 *
 * Copy rules: the whole panel should fit without scrolling. Prefer a short line
 * over a complete one — the specimen carries the explanation the prose used to.
 *
 * @module help-content
 */

const PRACTICES = {
  heading: 'What are experiments?',
  lede: 'Something small to try for a set window, with one thing to watch for — built around your situation, not generic advice.',
  specimen: {
    rows: [
      {
        annotation: 'what to try',
        parts: [
          { type: 'label', text: 'Experiment To Try' },
          { type: 'title', text: 'Communication in Conflict' },
          { type: 'body', text: 'When you notice tension rising, pause and name what you are feeling out loud.' }
        ]
      },
      {
        annotation: 'what we are testing',
        parts: [
          { type: 'callout', label: "What We're Testing", text: 'That saying it out loud will only make the argument worse.' }
        ]
      }
    ]
  },
  section: {
    header: 'What happens next?',
    numbered: true,
    items: [
      'Accept or skip — the buttons appear below the chat.',
      'It runs for the window you choose on the card, and lives on your Experiments page.',
      'Report back whenever you have something. We close it with what you found.',
      'Archive one any time — nothing is lost, and you can restore it later.'
    ]
  }
};

const INSIGHTS = {
  heading: 'What are insights?',
  lede: 'Patterns I have noticed in what you have shared — reflected back so you can see them.',
  specimen: {
    rows: [
      {
        annotation: 'what it is about',
        parts: [
          { type: 'label', text: 'How You Experience Love' }
        ]
      },
      {
        annotation: 'your result',
        parts: [
          { type: 'pills', primary: 'Acts of Care', secondary: 'Focused Attention' }
        ]
      },
      {
        annotation: 'what it means for you',
        parts: [
          { type: 'title', text: 'Actions speak louder than words' },
          { type: 'body', text: 'When your partner takes something off your plate, that is love in action.' }
        ]
      }
    ]
  },
  section: {
    header: 'Where do they come from?',
    numbered: false,
    items: [
      'Validated assessments — attachment, conflict style, money scripts.',
      'Patterns across many conversations — things you have told me over time.'
    ]
  }
};

const NOTES = {
  heading: 'What are partner notes?',
  lede: 'A short note to your partner, in your words, about something you worked out in a conversation.',
  specimen: {
    rows: [
      {
        annotation: 'what you worked out',
        parts: [
          { type: 'label', text: 'Note to your partner' },
          { type: 'title', text: 'What I go quiet about' },
          { type: 'body', text: 'When plans change late I get short with you, and it is not really about the plans.' }
        ]
      },
      {
        annotation: 'your words, not mine',
        parts: [
          { type: 'callout', label: 'Written As You', text: 'Every line traces back to something you said.' }
        ]
      },
      {
        annotation: 'nothing sends until you say so',
        parts: [
          { type: 'pills', primary: 'Send note', secondary: 'Discard note' }
        ]
      }
    ]
  },
  section: {
    header: 'How a note gets made',
    numbered: true,
    items: [
      'Work something through in a conversation.',
      'When you have got it, the option to write a note appears — or just ask.',
      'I draft it in your words. You read it first.',
      'Send it, steer it, or discard it. You can take a sent note back.'
    ]
  }
};

const CONTENT = {
  practices: PRACTICES,
  insights: INSIGHTS,
  notes: NOTES
};

/**
 * Look up the structured help content for a card type.
 * @param {'practices'|'insights'} type
 * @returns {object|null} Content object, or null for an unknown type
 */
export function getHelpContent(type) {
  return CONTENT[type] || null;
}

/**
 * Per-action blurbs for the chat-input Actions menu's "Learn about actions"
 * panel. Keyed by the menu's descriptor item keys — the panel renders ONLY the
 * keys currently on offer, so membership stays correct by construction. Data
 * only; the DOM is built by `chat/helpHintPanel.js`'s `buildActionsHelpPanel`.
 */
const ACTION_HELP = {
  practice: {
    icon: 'practices',
    // Title mirrors the Actions-menu descriptor label verbatim
    // (`advice-create-menu` → `advice-create-practice`, backend
    // `config/button-groups.js`) — the panel explains the item the user just
    // read, so the two must be changed together.
    title: 'Build an experiment',
    body: 'Turns what we are working on into one small experiment — something concrete to try for a set window, with one thing to watch for. If we have not talked enough yet, I will ask for what I need first.'
  },
  memo: {
    icon: 'mail',
    title: 'Write a note',
    body: 'Drafts a short note to your partner, in your words, about something you worked out here. You read it first — send it, steer it, or discard it. Nothing sends until you say so.'
  },
  explore: {
    icon: 'deep-explore',
    title: 'Deep exploration',
    body: 'A slower, deeper look at what sits underneath the topic — patterns, history, and what keeps it in place. I search extensively through everything you have shared, so replies take longer.'
  }
};

/**
 * Look up the Actions-menu blurb for a descriptor item key.
 * @param {'practice'|'memo'|'explore'} key
 * @returns {{icon: string, title: string, body: string}|null}
 */
export function getActionHelp(key) {
  return ACTION_HELP[key] || null;
}
